import { useMutation } from '@tanstack/react-query';
import { useWallet } from '@txnlab/use-wallet';
import algosdk from 'algosdk';
import axios from 'axios';
import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { algodClient, encodeNote } from '../utils/algo';
import { waitForConfirmation } from '../utils/txn-client';
import useAssetMetadataQuery from './useAssetMetadataQuery';
import useTraitSwapStore from './useTraitSwapStore';

export const useTraitSwapInitiateMutation = () => {
  const { signTransactions, sendTransactions } = useWallet();
  const { setInitiateSwapSuccess, setInitiateSwapUpdates } = useTraitSwapStore();

  return useMutation({
    mutationFn: async ({ address, selectedNfts, swappedLayers }) => {
      const suggestedParams = await algodClient.getTransactionParams().do();

      const transaction = algosdk.makeAssetTransferTxnWithSuggestedParamsFromObject({
        suggestedParams: suggestedParams,
        from: address,
        to: address,
        assetIndex: parseInt(process.env.NEXT_PUBLIC_TREAT_TOKEN_ID),
        amount: 0,
        note: encodeNote({
          address: address,
          selectedNfts,
          swappedLayers,
        }),
      });

      const encodedTransaction = algosdk.encodeUnsignedTransaction(transaction);
      const signedTransactions = await signTransactions([encodedTransaction]);
      const { id } = await sendTransactions(signedTransactions);

      const res = await fetch('/api/trait-swap/initiate', {
        method: 'POST',
        headers: {
          Authorization: process.env.NEXT_PUBLIC_API_KEY,
        },
        body: JSON.stringify({ transactionId: id, address, selectedNfts, swappedLayers }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error?.message || 'Failed to initiate trait swap');
      }

      return res.json();
    },
    onSuccess: (data) => {
      setInitiateSwapUpdates(data.updates);
      setInitiateSwapSuccess(true);
    },
    onError: (error) => {
      toast.error(error?.message || 'Failed to initiate trait swap');
      setInitiateSwapSuccess(false);
    },
  });
};

export const useTraitSwapFinalizeMutation = () => {
  const { signTransactions } = useWallet();
  const { reset, initiateSwapUpdates } = useTraitSwapStore();

  return useMutation({
    mutationFn: async ({ address, selectedNfts, swappedLayers }) => {
      if (!initiateSwapUpdates) {
        throw new Error('Trait swap not initiated');
      }

      const res = await fetch('/api/trait-swap/finalize', {
        method: 'POST',
        headers: {
          Authorization: process.env.NEXT_PUBLIC_API_KEY,
        },
        body: JSON.stringify({ address, selectedNfts, swappedLayers, initiateSwapUpdates }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error?.message || 'An error occurred');
      }

      const txns = await res.json();

      const { u, a } = txns;
      const decodedU = u.map((txn) => Uint8Array.from(Buffer.from(txn, 'base64')));
      const decodedA = a.map((txn) => Uint8Array.from(Buffer.from(txn, 'base64')));

      const signedTransactions = await signTransactions([...decodedU, ...decodedA]);

      const toSend = signedTransactions.map((txn) => {
        if (txn instanceof Uint8Array) {
          return txn;
        } else {
          return Uint8Array.from(txn);
        }
      });

      const sentTransaction = await algodClient.sendRawTransaction(toSend).do();

      await waitForConfirmation(sentTransaction.txId);

      try {
        await axios.post(
          '/api/trait-swap/record',
          {
            transactionId: sentTransaction.txId,
            selectedNfts,
            swappedLayers,
            address,
            updates: initiateSwapUpdates,
          },
          {
            headers: {
              Authorization: process.env.NEXT_PUBLIC_API_KEY,
            },
          }
        );
      } catch (error) {
        console.error(error);
      }

      return sentTransaction;
    },
    onSuccess: (data) => {
      toast.success('Trait swap successful');
      reset();
      location.reload();
    },
    onError: (error) => {
      console.log(error);
      toast.error(error?.message || 'Trait swap failed');
    },
  });
};

export const useTraitSwapResolver = () => {
  const { selectedNfts, updateMetadata, activeTab, setActiveTab } = useTraitSwapStore();
  const metadataOneQuery = useAssetMetadataQuery(selectedNfts[0].id);
  const metadataTwoQuery = useAssetMetadataQuery(selectedNfts[1].id);
  const metadataOne = metadataOneQuery.data;
  const metadataTwo = metadataTwoQuery.data;

  useEffect(() => {
    if (!metadataOne || !metadataTwo || !selectedNfts[0] || !selectedNfts[1]) return;

    updateMetadata([metadataOne, metadataTwo]);
  }, [metadataOne, metadataTwo]);

  useEffect(() => {
    if (activeTab === 'swap' && selectedNfts.length < 2) {
      setActiveTab('select');
    }
  }, [selectedNfts, activeTab]);
};
